/* eslint-disable consistent-return */
import React, { useMemo, useCallback, } from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import FieldLabel from "@shared/v2/FieldLabel";

import Modal from "@shared/v2/Modal";
import Dropdown from "@shared/v2/Dropdown";
import Banner from "@shared/v2/Banner";
import { TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";
import TextInput from "@shared/v2/TextInput";

import PersonDetailError from "../PersonDetailError";
import { LOCALITY_LIST } from "../../shared/constants";
import { getGoogleDetailsByPlaceIdApi, getGoogleMapsAutoCompleteApi } from "../../shared/api";

const STYLES = {
  errorClassName: "tw-absolute tw-bottom-[-20px]",
  textInputClassName: 'tw-flex-1 tw-relative',
  dropdownIndicator: (state) =>
    state?.selectProps?.inputValue || state.hasValue ? "#666666" : `${state.isFocused ? "#59C4C4" : "#C2C2C2"}`
}


const fetchAddressSuggestions = async (inputValue) => {
  const { res, err } = await getGoogleMapsAutoCompleteApi(inputValue);

  if (err) return [];

  return res.data.map((place) => ({
    label: place.description,
    value: place.placeId,
  }));
};

const debouncedFetch = debounce((inputValue, callback) => {
  fetchAddressSuggestions(inputValue).then(callback);
}, 500);


// This component is used to render the body of a modal for adding or editing an address.
const AddEditModalBody = ({ addressTypes, setAddressType, addressType, setStreetAddress, streetAddress, city, setCity, setLocality, locality, setPostalCode, postalCode, error, setCountry, specificTextInputError, standardized, setError }) => {
  const stateToMatch = useMemo(
    () =>
      [].concat(
        ...LOCALITY_LIST.map((category) =>
          category.options.map((option) => ({ label: option.value, value: option.value })),
        ),
      ),
    [LOCALITY_LIST],
  );

  const loadOptions = useCallback(
    (inputValue, callback) => {
      if (!inputValue) return callback([]);
      debouncedFetch(inputValue, callback);
    },
    []
  );

  return (
    <Modal.Body className="tw-flex tw-flex-col tw-gap-[24px]">
      <Banner
        containerClassName="tw-py-12px tw-px-16px tw-gap-16px !tw-bg-[#FAF1E6] !tw-border-[#FAE4C3] tw-rounded-4px"
        show={!standardized} >
        <TriangleExclamationSolidV6 className="tw-text-[#E5941B]" size='l' />
        <div className="tw-flex tw-flex-col tw-gap-4px tw-text-14d tw-text-[#AD6714]">
          <b>Invalid / Non Standardized Address</b>
          <span>This address is not standardized and may prevent certain features like mailers and home value estimates from working as intended.</span>
        </div>
      </Banner>
      {error && <PersonDetailError error={error} errorTitle={null} />}
      <div className="tw-flex tw-gap-[16px]">
        <Dropdown
          containerClassName="tw-w-[120px]"
          label="Address Type"
          options={addressTypes}
          onChange={(ad) => setAddressType(ad.value)}
          value={addressTypes.find((a) => a.value === addressType)}
          placeholder="Select"
        />
        <div className={STYLES.textInputClassName}>
          <FieldLabel
            label="Street Address"
            className="tw-mb-8px"
          />
          <AsyncSelect
            cacheOptions
            defaultOptions={false}
            placeholder=''
            onChange={async (selectedOption) => {
              if (selectedOption) {
                const { res, err } = await getGoogleDetailsByPlaceIdApi(selectedOption.value)

                if (res) {
                  const componentMap = {
                    streetNumber: '',
                    aptNumber: '', // will be filled from 'subpremise'
                    route: '',
                    city: '',      // will be filled from 'locality'
                    locality: '',  // will be filled from 'administrative_area_level_1'
                    psCode: '',     // will be filled from 'postal_code'
                    country: 'US'
                  };

                  res.data.addressComponents.forEach(component => {
                    const type = component.types[0];

                    switch (type) {
                      case 'street_number':
                        componentMap.streetNumber = component.shortName;
                        break;
                      case 'subpremise': {
                        const value = component.shortName.replace(/\s+/g, " ");
                        const hasPrefix = /^(#|apt\.?|suite\.?|unit\.?|bldg\.?)\s*\d+/i.test(value);

                        componentMap.aptNumber = hasPrefix ? value : `#${value}`;
                        break;
                      }
                      case 'route':
                        componentMap.route = component.shortName;
                        break;
                      case 'locality':
                        componentMap.city = component.shortName;
                        break;
                      case 'administrative_area_level_1':
                        componentMap.locality = component.shortName;
                        break;
                      case 'postal_code':
                        componentMap.psCode = component.shortName;
                        break;
                      case 'country':
                        componentMap.country = component.shortName;
                        break;
                      default:
                        break;
                    }
                  });

                  const street = [componentMap.streetNumber, componentMap.route, componentMap.aptNumber]
                    .filter(Boolean)
                    .join(' ');

                  setStreetAddress(street);
                  setCity(componentMap.city);
                  setLocality(componentMap.locality);
                  setPostalCode(componentMap.psCode);
                  setCountry(componentMap.country);
                  setError(null);
                  return;
                }
                setError(err.error || "Error fetching address details.");
              }
            }}

            onInputChange={(newValue, actionMeta) => {
              if (actionMeta.action === 'input-change') {
                setStreetAddress(newValue);
              }
              return newValue;
            }}
            value={streetAddress ? { label: streetAddress, value: streetAddress } : null}
            inputValue={streetAddress}
            blurInputOnSelect
            isClearable
            noOptionsMessage={() => null}
            loadOptions={loadOptions}
            backspaceRemovesValue
            components={{
              IndicatorSeparator: () => null,
              ClearIndicator: () => null,
              DropdownIndicator: () => null,
              LoadingIndicator: () => null,
            }}
            styles={{
              input: (styles) => ({
                ...styles,
                color: "#666666", padding: "0 20px 0 0", margin: "0",
                input: {
                  opacity: "1 !important",
                },
              }),

              valueContainer: (styles) => ({ ...styles, padding: "1px 14px 1px 16px" }),
              singleValue: (styles) => ({ ...styles, color: "#666666" }),
              control: (styles, state) => ({
                ...styles,
                background: "#F5F7F7",
                color: "#666666",
                borderRadius: "2px",
                boxShadow: "none",
                borderColor: state.isFocused ? "var(--text-input-border-active)" : "var(--text-input-border)",
                "&:hover": {
                  borderColor: "var(--text-input-border-hover)",
                  cursor: "text"
                },
                minHeight: "36px",
              }),
              dropdownIndicator: (styles, state) => ({
                ...styles,
                color: STYLES.dropdownIndicator(state),
                "&:focus": {
                  color: STYLES.dropdownIndicator(state),
                },
                padding: "0 12px 0 0",
              }),
              group: (styles) => ({ ...styles, padding: 0, margin: 0 }),
              groupHeading: () => ({
                color: "#666666",
                backgroundColor: "#F5F7F7",
                padding: "6px 12px",
              }),
              menuList: (styles) => ({ ...styles, maxHeight: "200px" }),
              menu: (styles) => ({
                ...styles,
              }),
              option: (styles, state) => ({
                ...styles,
                background: state.isSelected ? "#59C4C4" : `${state.isFocused ? "#cccccc" : "#fff"}`,
              }),
              placeholder: (styles) => ({ ...styles, whiteSpace: "nowrap", color: "#999999", margin: 0 }),
            }}
          />
        </div>

      </div>
      <div className="tw-flex tw-gap-[16px]">
        <TextInput
          label="City"
          onChange={(e) => setCity(e.target.value)}
          value={city}
          containerClassName={STYLES.textInputClassName}
          errorClassName={STYLES.errorClassName}
          error={(() => {
            if (specificTextInputError?.city) {
              return specificTextInputError.city;
            }

            if (city.length > 75) {
              return "Max 75 characters.";
            }

            return null;
          })()}
        />
        <Dropdown
          containerClassName="tw-w-[128px]"
          label="State / Province"
          options={LOCALITY_LIST}
          onChange={(option) => {
            setCountry(option?.country);
            setLocality(option?.value)
          }}
          value={stateToMatch.find((o) => o.value === locality)}
          placeholder="Select"
          isSearchable
          menuShouldComeToFront
        />
        <TextInput
          label="ZIP / Postal Code"
          containerClassName='tw-w-[128px] tw-relative'
          onChange={(e) => setPostalCode(e.target.value)}
          value={postalCode}
          error={(() => {
            if (specificTextInputError?.postalCode) {
              return specificTextInputError.postalCode;
            }

            if (postalCode?.length > 10) {
              return "Max 10 characters.";
            }

            return null;
          })()}
          errorClassName={STYLES.errorClassName}
        />
      </div>
    </Modal.Body>
  );
}

export default AddEditModalBody;

AddEditModalBody.propTypes = {
  addressTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  setAddressType: PropTypes.func.isRequired,
  addressType: PropTypes.string.isRequired,
  setStreetAddress: PropTypes.func.isRequired,
  streetAddress: PropTypes.string,
  city: PropTypes.string,
  setError: PropTypes.func.isRequired,
  setCountry: PropTypes.func.isRequired,
  setCity: PropTypes.func.isRequired,
  stateToMatch: PropTypes.shape({}).isRequired,
  setLocality: PropTypes.func.isRequired,
  locality: PropTypes.string,
  setPostalCode: PropTypes.func.isRequired,
  postalCode: PropTypes.string,
  standardized: PropTypes.bool,
  error: PropTypes.string,
  specificTextInputError: PropTypes.shape({
    streetAddress: PropTypes.string,
    city: PropTypes.string,
    postalCode: PropTypes.string,
  }),
};

AddEditModalBody.defaultProps = {
  standardized: false,
  streetAddress: "",
  city: "",
  locality: "",
  postalCode: "",
  error: null,
  specificTextInputError: null,
};
